import React, { useState, useEffect } from "react";
import { useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Alert,
  CircularProgress,
  useTheme,
  Paper,
  Divider,
  Avatar,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemButton,
  ListItemAvatar,
  ListItemText,
  Pagination,
  Stack,
} from '@mui/material';
import axiosInstance from "../utils/axiosInstance";
import { hasPermission } from '../utils/permissionUtils';
import { formatDistanceToNow, parseISO } from 'date-fns';

export default function ChatHistory() {
  const theme = useTheme();
  const [activeChat, setActiveChat] = useState("");
  const [documents, setDocuments] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [chatsPerPage] = useState(10);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { chatbotId: paramsChatbotId } = useParams();

  // Permission states
  const [canView, setCanView] = useState(false);
  const [checkingPermissions, setCheckingPermissions] = useState(true);

  // Check permissions
  useEffect(() => {
    const checkPermissions = async () => {
      setCheckingPermissions(true);
      try {
        const viewPerm = await hasPermission('chat-history', 'view');
        setCanView(viewPerm);
      } catch (error) {
        console.error('Error checking permissions:', error);
        setCanView(false);
      } finally {
        setCheckingPermissions(false);
      }
    };

    checkPermissions();
  }, []);

  useEffect(() => {
    document.title = 'Sessions | KlonIT Web Application';
    if (canView && !checkingPermissions) {
      fetchDocuments();
    }
  }, [canView, checkingPermissions]);

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      const params = paramsChatbotId ? { chatbot_id: paramsChatbotId } : {};
      const response = await axiosInstance.get(`/get-chat-history`, { params });
      const responseData = typeof response.data.data === 'string' 
        ? JSON.parse(response.data.data) 
        : response.data.data;

      setDocuments(responseData);
      setError(null);
    } catch (error) {
      console.error("Error fetching chat history:", error);
      if (error.response?.status === 401) {
        setError("Authentication error: " + error.response.data.message);
      } else {
        setError("Error fetching data: " + (error.response?.data?.message || error.message));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleChatClick = (chatId) => {
    setActiveChat(chatId);
  };

  const formatRelativeDate = (dateString) => {
    if (dateString === 'N/A' || !dateString) {
      return 'Invalid date';
    }
    const date = parseISO(dateString);
    if (isNaN(date)) {
      return 'Invalid date';
    }
    return formatDistanceToNow(date, { addSuffix: true }).replace('about ', '');
  };

  // Sort chat IDs in descending order
  const sortedChatIds = Object.keys(documents).sort((a, b) => {
    const dateA = documents[a][0]?.created_date ? parseISO(documents[a][0].created_date) : new Date(0);
    const dateB = documents[b][0]?.created_date ? parseISO(documents[b][0].created_date) : new Date(0);
    return dateB - dateA;
  });

  // Pagination logic
  const totalChats = sortedChatIds.length;
  const totalPages = Math.ceil(totalChats / chatsPerPage);
  const indexOfLastChat = currentPage * chatsPerPage;
  const indexOfFirstChat = indexOfLastChat - chatsPerPage;
  const currentChats = sortedChatIds.slice(indexOfFirstChat, indexOfLastChat);

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };

  // Show loading state while checking permissions
  if (checkingPermissions) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", p: 5 }}>
        <CircularProgress size={30} />
        <Typography ml={2}>Checking permissions...</Typography>
      </Box>
    );
  }

  // Show access denied if no view permission
  if (!canView) {
    return (
      <Box sx={{ p: 3, mt: 10 }}>
        <Alert severity="error">
          <strong>Access Denied</strong>
          <br />
          You don't have permission to view chatbot history. Please contact your administrator.
        </Alert>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", p: 5, mt: 10 }}>
        <CircularProgress size={30} />
        <Typography ml={2}>Loading chat sessions...</Typography>
      </Box>
    );
  }

  return (
    <Paper sx={{ p: 3, mt: 10, mx: 'auto', maxWidth: '1400px', bgcolor: 'background.paper' }}>
      <Typography variant="h5" component="h1" sx={{ mb: 3 }} color="textPrimary">
        Chat <strong>Sessions</strong>
      </Typography>
      <Divider sx={{ mb: 3 }} />

      {currentChats.length > 0 ? (
        <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', md: 'row' } }}>
          {/* Sessions List */}
          <Paper sx={{ flex: '0 0 350px', p: 2, bgcolor: 'background.default' }}>
            <Typography variant="h6" sx={{ mb: 2 }} color="text.primary">
              Sessions
            </Typography>
            <List sx={{ p: 0 }}>
              {currentChats.map(chatId => (
                <ListItem 
                  key={chatId} 
                  disablePadding
                  sx={{ 
                    mb: 1,
                    borderRadius: 1,
                    bgcolor: activeChat === chatId ? 'action.selected' : 'transparent',
                  }}
                >
                  <ListItemButton 
                    onClick={() => handleChatClick(chatId)}
                    sx={{ 
                      borderRadius: 1,
                      '&:hover': {
                        bgcolor: 'action.hover',
                      }
                    }}
                  >
                    <ListItemAvatar>
                      <Avatar src="/images/avatars/avatar.jpg" alt="avatar" />
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Typography variant="body1" fontWeight={600} noWrap>
                          {chatId}
                        </Typography>
                      }
                      secondary={
                        <Typography variant="caption" color="text.secondary">
                          {formatRelativeDate(documents[chatId][0]?.created_date)}
                        </Typography>
                      }
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
            
            {/* Pagination */}
            {totalPages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <Pagination 
                  count={totalPages} 
                  page={currentPage} 
                  onChange={handlePageChange}
                  color="primary"
                  size="small"
                />
              </Box>
            )}
          </Paper>

          {/* Chat Messages */}
          <Box sx={{ flex: 1 }}>
            {activeChat ? (
              <Paper sx={{ p: 3, bgcolor: 'background.default', minHeight: '500px' }}>
                <Typography variant="h6" sx={{ mb: 3 }} color="text.primary">
                  Conversation
                </Typography>
                <Stack spacing={2}>
                  {documents[activeChat]
                    .slice()
                    .sort((a, b) => new Date(b.created_date) - new Date(a.created_date))
                    .map((message, index) => (
                      <React.Fragment key={index}>
                        {/* User Question */}
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                          <Card sx={{ maxWidth: '70%', bgcolor: theme.palette.mode === 'dark' ? '#1e293b' : '#e3f2fd' }}>
                            <CardContent>
                              <Typography variant="body1">
                                {message.questions}
                              </Typography>
                            </CardContent>
                          </Card>
                          <Avatar src="/images/avatars/avatar.jpg" alt="user" />
                        </Box>

                        {/* Bot Answer */}
                        <Box sx={{ display: 'flex', justifyContent: 'flex-start', gap: 2 }}>
                          <Avatar src="/images/avatars/avatar_img (1).png" alt="bot" />
                          <Card sx={{ maxWidth: '70%', bgcolor: 'background.paper' }}>
                            <CardContent>
                              <Typography variant="body1">
                                {message.answers}
                              </Typography>
                            </CardContent>
                          </Card>
                        </Box>
                      </React.Fragment>
                    ))}
                </Stack>
              </Paper>
            ) : (
              <Paper sx={{ p: 5, bgcolor: 'background.default', minHeight: '500px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Box textAlign="center">
                  <i className="fa fa-comments-o fa-3x text-muted" />
                  <Typography mt={2} color="text.secondary">
                    Select a session to view conversation
                  </Typography>
                </Box>
              </Paper>
            )}
          </Box>
        </Box>
      ) : (
        <Box textAlign="center" py={8}>
          <i className="fa fa-comments-o fa-3x text-muted" />
          <Typography mt={2} variant="h6" color="text.primary">
            No Chat History Found
          </Typography>
          <Typography color="text.secondary">
            Start a conversation to see chat history here
          </Typography>
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mt: 3 }}>
          <i className="fa fa-exclamation-triangle" /> {error}
        </Alert>
      )}
    </Paper>
  );
}